import type { AppRouter } from '@alison/api/router';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import type { TRPCClientErrorLike } from '@trpc/client';
import type { inferInput, inferOutput } from '@trpc/tanstack-react-query';
import { useTRPC } from '../../../lib/trpc';
import type { MetricFormat } from '../../creatives/data/format';

type TRPC = ReturnType<typeof useTRPC>;
type QueryResult<TData> = UseQueryResult<TData, TRPCClientErrorLike<AppRouter>>;

export type ElementsTableBase = 'all' | 'alison-picks';

export type ElementsCategoriesScope = inferInput<TRPC['elements']['howToCategories']>;
export type ElementsCategoriesData = inferOutput<TRPC['elements']['howToCategories']>;
export type ElementCategoryRow = ElementsCategoriesData[number];

export type ElementsTableScope = inferInput<TRPC['elements']['elementsDrilldown']>;
export type ElementsTableData = inferOutput<TRPC['elements']['elementsDrilldown']>;
export type ElementLeafRow = ElementsTableData['rows'][number];

export type ElementsCategoryExamplesScope = inferInput<TRPC['elements']['howToCategoryExamples']>;
export type ElementsCategoryExamplesData = inferOutput<TRPC['elements']['howToCategoryExamples']>;

export type MetricCatalog = inferOutput<TRPC['filters']['metrics']>;

export function useElementsCategories(
  scope: ElementsCategoriesScope,
  enabled: boolean,
): QueryResult<ElementsCategoriesData> {
  const trpc = useTRPC();
  return useQuery({ ...trpc.elements.howToCategories.queryOptions(scope), enabled });
}

export function useElementsCategoryExamples(
  scope: ElementsCategoryExamplesScope,
  enabled: boolean,
): QueryResult<ElementsCategoryExamplesData> {
  const trpc = useTRPC();
  return useQuery({ ...trpc.elements.howToCategoryExamples.queryOptions(scope), enabled });
}

export function useElementsTable(
  scope: ElementsTableScope,
  activeBase: ElementsTableBase,
  enabled: boolean,
): QueryResult<ElementsTableData> {
  const trpc = useTRPC();
  const options =
    activeBase === 'all'
      ? trpc.elements.elementsDrilldown.queryOptions(scope)
      : trpc.elements.elementsAlisonPicks.queryOptions(scope);
  return useQuery({ ...options, enabled });
}

export function useMetricCatalog(accountIds: number[]): QueryResult<MetricCatalog> {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.filters.metrics.queryOptions({ accountIds }),
    enabled: accountIds.length > 0,
  });
}

export function metricFormatFor(metric: MetricCatalog[string] | undefined): MetricFormat {
  if (metric?.money === true) return 'compact_currency';
  if (metric?.percent === true) return 'percentage';
  if (metric?.shape === 'ratio') return 'ratio';
  return 'number';
}
